import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "@dr.pogodin/react-helmet";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";
import InstallBanner from "@/components/InstallBanner";
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ListingsPage from "@/pages/ListingsPage";
import ListingDetailPage from "@/pages/ListingDetailPage";
import CreateListingPage from "@/pages/CreateListingPage";
import DashboardPage from "@/pages/DashboardPage";
import MessagesPage from "@/pages/MessagesPage";
import EditListingPage from "@/pages/EditListingPage";
import AdminPage from "@/pages/AdminPage";
import AboutPage from "@/pages/AboutPage";
import FAQPage from "@/pages/FAQPage";
import ContactPage from "@/pages/ContactPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import BoostSuccessPage from "@/pages/BoostSuccessPage";
import NotFoundPage from "@/pages/NotFoundPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import ServicePublicationPage from "@/pages/ServicePublicationPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import BlogListPage from "@/pages/BlogListPage";
import BlogArticlePage from "@/pages/BlogArticlePage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-neutral-300 border-t-[#1F3F72] rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/connexion" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-neutral-300 border-t-[#1F3F72] rounded-full animate-spin" /></div>;
  if (!user || user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <div className="min-h-screen flex flex-col bg-background font-body">
      <Header />
      <main className="flex-1 flex flex-col">
        <PageTransition>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/connexion" element={<LoginPage />} />
          <Route path="/inscription" element={<RegisterPage />} />
          <Route path="/annonces" element={<ListingsPage />} />
          <Route path="/annonces/:id" element={<ListingDetailPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
          <Route path="/publier" element={<ProtectedRoute><CreateListingPage /></ProtectedRoute>} />
          <Route path="/annonces/:id/modifier" element={<ProtectedRoute><EditListingPage /></ProtectedRoute>} />
          <Route path="/tableau-de-bord" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
          <Route path="/boost/success" element={<ProtectedRoute><BoostSuccessPage /></ProtectedRoute>} />
          <Route path="/boost/cancel" element={<Navigate to="/tableau-de-bord" replace />} />
          <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
          <Route path="/comment-ca-marche" element={<HowItWorksPage />} />
          <Route path="/a-propos" element={<AboutPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/conditions-utilisation" element={<TermsPage />} />
          <Route path="/politique-confidentialite" element={<PrivacyPage />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/desinscription" element={<UnsubscribePage />} />
          <Route path="/service-publication" element={<ServicePublicationPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </PageTransition>
      </main>
      <ConditionalFooter />
      <InstallBanner />
      <Toaster richColors position="top-right" />
    </div>
  );
}

function ConditionalFooter() {
  const { pathname } = useLocation();
  if (pathname === '/messages') return null;
  return <Footer />;
}

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
